import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  X,
  Bell,
  UserPlus,
  Mail,
  MessageSquare,
  DollarSign,
  Calendar,
  Activity,
  FileText,
  StickyNote,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToastStore } from '@/store/toastStore';
import type {
  Toast as ToastType,
  ToastVariant,
  ToastNotificationGroup,
} from '@/types/common.types';

// ─── Per-toast config ─────────────────────────────────────────────────────────

const variantConfig: Record<
  Exclude<ToastVariant, 'notification'>,
  { icon: React.ElementType; containerClass: string; iconClass: string }
> = {
  success: {
    icon: CheckCircle2,
    containerClass: 'border-green-200 bg-green-50',
    iconClass: 'text-green-600',
  },
  error: {
    icon: XCircle,
    containerClass: 'border-red-200 bg-red-50',
    iconClass: 'text-red-600',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'border-amber-200 bg-amber-50',
    iconClass: 'text-amber-600',
  },
  info: {
    icon: Info,
    containerClass: 'border-blue-200 bg-blue-50',
    iconClass: 'text-blue-600',
  },
};

// Pop-up notification toasts (legacy parity with `notification_display.php`).
// The accent colors echo the inline `<strong style="color:...">` styles
// the legacy PHP renderer emitted per typeID family.
const notificationGroupConfig: Record<
  ToastNotificationGroup,
  { icon: React.ElementType; containerClass: string; iconClass: string }
> = {
  lead:     { icon: UserPlus,       containerClass: 'border-blue-200 bg-blue-50',     iconClass: 'text-blue-700' },
  email:    { icon: Mail,           containerClass: 'border-amber-200 bg-amber-50',   iconClass: 'text-amber-700' },
  sms:      { icon: MessageSquare,  containerClass: 'border-rose-200 bg-rose-50',     iconClass: 'text-rose-700' },
  message:  { icon: MessageSquare,  containerClass: 'border-indigo-200 bg-indigo-50', iconClass: 'text-indigo-700' },
  payment:  { icon: DollarSign,     containerClass: 'border-red-200 bg-red-50',       iconClass: 'text-red-700' },
  appt:     { icon: Calendar,       containerClass: 'border-green-200 bg-green-50',   iconClass: 'text-green-700' },
  clinical: { icon: Activity,       containerClass: 'border-amber-200 bg-amber-50',   iconClass: 'text-amber-800' },
  note:     { icon: StickyNote,     containerClass: 'border-red-200 bg-red-50',       iconClass: 'text-red-700' },
  eform:    { icon: FileText,       containerClass: 'border-orange-200 bg-orange-50', iconClass: 'text-orange-700' },
  report:   { icon: FileText,       containerClass: 'border-teal-200 bg-teal-50',     iconClass: 'text-teal-700' },
  unknown:  { icon: Bell,           containerClass: 'border-slate-200 bg-slate-50',   iconClass: 'text-slate-700' },
};

const DEFAULT_DURATION = 5000;

function resolveToastStyle(toast: ToastType): { icon: React.ElementType; containerClass: string; iconClass: string } {
  if (toast.variant === 'notification') {
    return notificationGroupConfig[toast.notificationGroup ?? 'unknown'];
  }
  return variantConfig[toast.variant];
}

// ─── Single toast item ────────────────────────────────────────────────────────

interface ToastItemProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

function TopNotificationToast({ toast, onDismiss, visible }: ToastItemProps & { visible: boolean }) {
  const isError = toast.variant === 'error' || toast.variant === 'warning';
  const isClickable = typeof toast.onClick === 'function';

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex items-center gap-2 px-7 py-3 rounded-b-lg shadow-[0_4px_12px_rgba(0,0,0,0.2)] max-w-[600px]',
        // Only the toast itself takes pointer events; the container is inert so the
        // full-width band it spans below md cannot swallow taps meant for the page.
        'pointer-events-auto',
        // Tighter padding below md. Layered as a `max-md:` escape rather than a
        // second size map so desktop keeps px-7/py-3 untouched — same reasoning as
        // the touch floor in Button.tsx.
        'max-md:px-4 max-md:py-2.5',
        'text-white text-sm font-medium',
        'transition-transform duration-300 ease-out',
        isError ? 'bg-[#dc3545]' : 'bg-[#28a745]',
        visible ? 'translate-y-0' : '-translate-y-full',
      )}
    >
      <span className="text-base">{isError ? '✕' : '✓'}</span>

      {isClickable ? (
        <button
          type="button"
          onClick={() => { toast.onClick?.(); onDismiss(toast.id); }}
          className="flex-1 min-w-0 max-md:break-words text-left cursor-pointer focus:outline-none text-white"
        >
          <span>{toast.title}</span>
          {toast.description && <span className="ml-1 opacity-90">{toast.description}</span>}
        </button>
      ) : (
        <div className="flex-1 min-w-0 max-md:break-words">
          <span>{toast.title}</span>
          {toast.description && <span className="ml-1 opacity-90">{toast.description}</span>}
        </div>
      )}

      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-white/70 hover:text-white focus:outline-none"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function CardToast({ toast, onDismiss }: ToastItemProps) {
  const { icon: Icon, containerClass, iconClass } = resolveToastStyle(toast);
  const isClickable = typeof toast.onClick === 'function';

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex w-80 items-start gap-3 rounded-lg border px-4 py-3 shadow-md',
        // `w-80` is 320px, which fits a 375px screen with the container's inset but
        // overflows anything narrower — a 320px iPhone SE, or a phone in a split view.
        // `max-w-full` costs nothing at any width where 320px already fits, so it needs
        // no breakpoint prefix and leaves desktop identical.
        'max-w-full',
        'animate-in slide-in-from-top-2 duration-200',
        containerClass,
      )}
    >
      <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', iconClass)} aria-hidden="true" />

      {isClickable ? (
        <button
          type="button"
          onClick={() => { toast.onClick?.(); onDismiss(toast.id); }}
          className="flex-1 min-w-0 text-left cursor-pointer focus:outline-none"
        >
          <p className="text-sm font-medium text-slate-900">{toast.title}</p>
          {toast.description && <p className="mt-0.5 text-xs text-slate-700 line-clamp-3">{toast.description}</p>}
        </button>
      ) : (
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900">{toast.title}</p>
          {toast.description && <p className="mt-0.5 text-xs text-slate-600">{toast.description}</p>}
        </div>
      )}

      <button
        type="button"
        onClick={() => { toast.onClose?.(); onDismiss(toast.id); }}
        className="flex-shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const defaultDuration = DEFAULT_DURATION;
  const duration = toast.duration === undefined ? defaultDuration : toast.duration;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [visible, setVisible] = React.useState(false);

  const dismiss = React.useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  }, [toast.id, onDismiss]);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    if (duration === 0) return;
    timerRef.current = setTimeout(dismiss, duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `toast.description` is in here so that a toast refreshed in place by the
    // store's variant+title de-duplication restarts its dismissal window instead
    // of expiring on the timer the first message started. Re-running is cheap:
    // setVisible(true) is a no-op once visible, and the cleanup clears the
    // superseded timer. An identical repeat changes nothing and so does not
    // extend the window, which matches the previous behaviour.
  }, [toast.id, toast.description, duration, dismiss]);

  if (toast.variant === 'notification') {
    return <CardToast toast={toast} onDismiss={onDismiss} />;
  }

  return <TopNotificationToast toast={toast} onDismiss={dismiss} visible={visible} />;
}

// ─── Toast container ──────────────────────────────────────────────────────────

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const removeToast = useToastStore((s) => s.removeToast);

  if (toasts.length === 0) return null;

  const actionToasts = toasts.filter((t) => t.variant !== 'notification');
  const notificationToasts = toasts.filter((t) => t.variant === 'notification');

  return ReactDOM.createPortal(
    <>
      {actionToasts.length > 0 && (
        // `fixed` + `left-1/2` + no width is a trap: shrink-to-fit gives this box
        // (containing block − left) = half the viewport, and -translate-x-1/2 is a
        // paint-time transform that restores none of it. At 375px that left ~79px
        // for text and wrapped a one-line banner into six. Below md the box is
        // released to the full width instead; md+ keeps the original geometry
        // exactly, because widening it there would let long error toasts render
        // wider than they do today in the 1024–1199px band.
        //
        // pointer-events-none is load-bearing, not tidiness: without it the
        // full-width band would sit over the phone header's menu button for the
        // toast's whole life. Each toast re-enables events on itself.
        <div
          className="fixed top-0 left-1/2 -translate-x-1/2 z-[10001] flex flex-col gap-2 items-center pointer-events-none max-md:left-0 max-md:right-0 max-md:translate-x-0 max-md:px-2"
          aria-label="Action alerts"
        >
          {actionToasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
          ))}
        </div>
      )}
      {notificationToasts.length > 0 && (
        <div
          // Unlike the action stack above, this one has no `left-1/2` and so was never
          // half-width — shrink-to-fit resolves it against `vw - 16px`. What it does
          // need is the home-indicator clearance: `bottom-4` alone puts a card inside
          // the ~34px gesture band on a notched phone. `pb-safe` resolves to 0 wherever
          // no inset is reported, so desktop is unchanged. `max-md:left-4` lets a card
          // use the full width on a phone rather than being pinned to a 320px column.
          className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 items-end pb-safe max-md:left-4"
          aria-label="Notifications"
        >
          {notificationToasts.map((t) => (
            <ToastItem key={t.id} toast={t} onDismiss={removeToast} />
          ))}
        </div>
      )}
    </>,
    document.body,
  );
}
